'use client';

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Typography,
  Box,
  Divider,
  Grid,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Instagram,
  Facebook,
  Delete,
  Sync,
  Visibility,
  Share,
  TrendingUp,
  CheckCircle,
  Error,
  Close,
} from '@mui/icons-material';
import { toast } from 'sonner';
import { SocialPlatform } from '../api/social.api';

interface PostingManagerProps {
  postedContents: any[];
  onRefresh: () => Promise<void>;
  onEditPost: (post: any) => void;
  onDeletePost: (postId: string, platform: string) => Promise<void>;
  onSyncPosts: () => Promise<void>;
  platformFilter: string;
  onPlatformFilterChange: (platform: string) => void;
}

const PostingManager: React.FC<PostingManagerProps> = ({
  postedContents,
  onRefresh,
  onEditPost,
  onDeletePost,
  onSyncPosts,
  platformFilter,
  onPlatformFilterChange,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:600px)');
  
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [selectedPost, setSelectedPost] = useState<any>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [postToDelete, setPostToDelete] = useState<{
    postId: string;
    platform: string;
  } | null>(null);

  /**
   * 플랫폼별 아이콘 및 색상 정보를 반환
   */
  const getPlatformInfo = (platform: string) => {
    switch (platform) {
      case 'Facebook':
        return { icon: Facebook, color: '#1877F2', name: 'Facebook' };
      case 'Instagram':
        return { icon: Instagram, color: '#E4405F', name: 'Instagram' };
      case 'Threads':
        return {
          icon: () => (
            <svg
              aria-label='Threads'
              fill='currentColor'
              height='18'
              role='img'
              viewBox='0 0 192 192'
              width='18'
            >
              <path d='M141.537 88.9883C140.71 88.5919 139.87 88.2104 139.019 87.8451C137.537 60.5382 122.616 44.905 97.5619 44.745C97.4484 44.7443 97.3355 44.7443 97.222 44.7443C82.2364 44.7443 69.7731 51.1409 62.102 62.7807L75.881 72.2328C81.6116 63.5383 90.6052 61.6848 97.2286 61.6848C97.3051 61.6848 97.3819 61.6848 97.4576 61.6855C105.707 61.7381 111.932 64.1366 115.961 68.814C118.893 72.2193 120.854 76.925 121.825 82.8638C114.511 81.6207 106.601 81.2385 98.145 81.7233C74.3247 83.0954 59.0111 96.9879 60.0396 116.292C60.5615 126.084 65.4397 134.508 73.775 140.011C80.8224 144.663 89.899 146.938 99.3323 146.423C111.79 145.74 121.563 140.987 128.381 132.296C133.559 125.696 136.834 117.143 138.28 106.366C144.217 109.949 148.617 114.664 151.047 120.332C155.179 129.967 155.42 145.8 142.501 158.708C131.182 170.016 117.576 174.908 97.0135 175.059C74.2042 174.89 56.9538 167.575 45.7381 153.317C35.2355 139.966 29.8077 120.682 29.6052 96C29.8077 71.3178 35.2355 52.0336 45.7381 38.6827C56.9538 24.4249 74.2039 17.11 97.0132 16.9405C119.988 17.1113 137.539 24.4614 149.184 38.788C154.894 45.8136 159.199 54.6488 162.037 64.9503L178.184 60.6422C174.744 47.9622 169.331 37.0357 161.965 27.974C147.036 9.60668 125.202 0.195148 97.0695 0H96.9569C68.8816 0.19447 47.2921 9.6418 32.7883 28.0793C19.8819 44.4864 13.2244 67.3157 13.0007 95.9325L13 96L13.0007 96.0675C13.2244 124.684 19.8819 147.514 32.7883 163.921C47.2921 182.358 68.8816 191.806 96.9569 192H97.0695C122.03 191.827 139.624 185.292 154.118 170.811C173.081 151.866 172.51 128.119 166.26 113.541C161.776 103.087 153.227 94.5962 141.537 88.9883ZM98.4405 129.507C88.0005 130.095 77.1544 125.409 76.6196 115.372C76.2232 107.93 81.9158 99.626 99.0812 98.6368C101.047 98.5234 102.976 98.468 104.871 98.468C111.106 98.468 116.939 99.0737 122.242 100.233C120.264 124.935 108.662 128.946 98.4405 129.507Z' />
            </svg>
          ),
          color: '#000',
          name: 'Threads',
        };
      default:
        return { icon: Share, color: '#666', name: platform };
    }
  };

  /**
   * 포스트 상태를 확인하여 표시할 정보 반환
   */
  const getPostStatus = (post: any) => {
    if (post.error) {
      return { status: 'error', label: '오류', color: 'text-red-600 bg-red-50' };
    }
    if (post.isActive === false) {
      return { status: 'inactive', label: '비활성', color: 'text-yellow-600 bg-yellow-50' };
    }
    return { status: 'active', label: '활성', color: 'text-green-600 bg-green-50' };
  };

  /**
   * 포스팅 삭제 확인 다이얼로그 열기
   */
  const handleDeleteClick = (postId: string, platform: string) => {
    setPostToDelete({ postId, platform });
    setDeleteConfirmOpen(true);
  };

  /**
   * 포스팅 삭제 실행
   */
  const handleDeleteConfirm = async () => {
    if (!postToDelete) return;

    setLoading(true);
    try {
      await onDeletePost(postToDelete.postId, postToDelete.platform);
      toast.success('포스팅이 삭제되었습니다.');
      setDeleteConfirmOpen(false);
      setPostToDelete(null);
    } catch (error) {
      console.error('삭제 오류:', error);
      toast.error('포스팅 삭제에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * 동기화 실행
   */
  const handleSync = async () => {
    setSyncing(true);
    try {
      await onSyncPosts();
      toast.success('포스팅 동기화가 완료되었습니다.');
    } catch (error) {
      console.error('동기화 오류:', error);
      toast.error('동기화에 실패했습니다.');
    } finally {
      setSyncing(false);
    }
  };

  /**
   * 날짜 포맷팅
   */
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  /**
   * 플랫폼별 포스트 URL 생성
   */
  const generatePostUrl = (platform: string, postId: string): string => {
    switch (platform) {
      case 'Facebook':
        return `https://www.facebook.com/${postId}`;
      case 'Instagram':
        return `https://instagram.com/p/${postId}`;
      case 'Threads':
        return `https://www.threads.net/t/${postId}`;
      default:
        return '';
    }
  };

  return (
    <Box sx={{ mx: 'auto', p: 3 }}>
      {/* 헤더 */}
      <Card
        sx={{
          mb: isMobile ? 2 : 4,
          borderRadius: isMobile ? 2 : 4,
          background: '#42424a',
          color: 'white',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: isMobile ? 2 : 4 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: isMobile ? 'flex-start' : 'center',
              justifyContent: 'space-between',
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? 2 : 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 1.5 : 2 }}>
              <Box
                sx={{
                  p: isMobile ? 1.5 : 2,
                  borderRadius: isMobile ? 2 : 3,
                  bgcolor: 'rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <TrendingUp sx={{ fontSize: isMobile ? 24 : 32 }} style={{color: '#fff'}}/>
              </Box>
              <Box>
                <Typography 
                  variant={isMobile ? 'h6' : 'h4'} 
                  fontWeight='700' 
                  sx={{ mb: isMobile ? 0.5 : 1 }} 
                  style={{color: '#fff'}}
                >
                  📊 포스팅 관리
                </Typography>
                <Typography 
                  variant={isMobile ? 'body2' : 'body1'} 
                  sx={{ opacity: 0.9 }} 
                  style={{color: '#fff'}}
                >
                  발행된 소셜 포스팅을 관리하고 성과를 확인하세요
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: isMobile ? '100%' : 'auto' }}>
              <Button
                variant='outlined'
                startIcon={syncing ? <CircularProgress size={isMobile ? 14 : 16} /> : <Sync />}
                onClick={handleSync}
                disabled={syncing}
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  color: 'white',
                  borderColor: 'rgba(255,255,255,0.5)',
                  fontSize: isMobile ? '0.75rem' : '0.875rem',
                  flex: isMobile ? 1 : 'none',
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)',
                  },
                }}
              >
                {syncing ? '동기화 중...' : '동기화'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* 필터 */}
      <Card sx={{ mb: isMobile ? 2 : 3, borderRadius: isMobile ? 2 : 3 }}>
        <CardContent sx={{ p: isMobile ? 2 : 3 }}>
          <Grid container spacing={isMobile ? 2 : 3} alignItems='center'>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
                <InputLabel sx={{ fontWeight: 600, color: 'primary.main', fontSize: isMobile ? '0.875rem' : '1rem' }}>
                  🌐 플랫폼 필터
                </InputLabel>
                <Select
                  value={platformFilter}
                  label='🌐 플랫폼 필터'
                  onChange={(e) => onPlatformFilterChange(e.target.value)}
                  sx={{
                    borderRadius: isMobile ? 2 : 3,
                    minHeight: isMobile ? 40 : 56,
                    fontSize: isMobile ? '0.875rem' : '1rem',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderWidth: 2,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  <MenuItem value='all' sx={{ fontWeight: 500, fontSize: isMobile ? '0.875rem' : '1rem' }}>
                    🌐 모든 플랫폼
                  </MenuItem>
                                    <MenuItem value='Facebook' sx={{ fontWeight: 500, fontSize: isMobile ? '0.875rem' : '1rem' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {React.createElement(getPlatformInfo('Facebook').icon, { sx: { color: getPlatformInfo('Facebook').color, fontSize: isMobile ? 18 : 20 } })}
                      {getPlatformInfo('Facebook').name}
                    </Box>
                  </MenuItem>
                  <MenuItem value='Instagram' sx={{ fontWeight: 500, fontSize: isMobile ? '0.875rem' : '1rem' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {React.createElement(getPlatformInfo('Instagram').icon, { sx: { color: getPlatformInfo('Instagram').color, fontSize: isMobile ? 18 : 20 } })}
                      {getPlatformInfo('Instagram').name}
                    </Box>
                  </MenuItem>
                  <MenuItem value='Threads' sx={{ fontWeight: 500, fontSize: isMobile ? '0.875rem' : '1rem' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {React.createElement(getPlatformInfo('Threads').icon, { style: { color: getPlatformInfo('Threads').color, fontSize: isMobile ? 18 : 20 } })}
                      {getPlatformInfo('Threads').name}
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: isMobile ? 1 : 2,
                flexWrap: isMobile ? 'wrap' : 'nowrap',
                justifyContent: isMobile ? 'center' : 'flex-start'
              }}>
                <Typography variant={isMobile ? 'caption' : 'body2'} color='text.secondary'>
                  총 {postedContents.length}개 포스팅
                </Typography>
                <Divider orientation='vertical' flexItem />
                <Typography variant={isMobile ? 'caption' : 'body2'} color='success.main'>
                  활성:{' '}
                  {postedContents.filter((p) => p.isActive !== false).length}개
                </Typography>
                <Typography variant={isMobile ? 'caption' : 'body2'} color='error.main'>
                  비활성:{' '}
                  {postedContents.filter((p) => p.isActive === false).length}개
                </Typography>
              </Box>
            </Grid>

           
          </Grid>
        </CardContent>
      </Card>

      {/* 포스팅 목록 */}
      {postedContents.length === 0 ? (
        <Card sx={{ borderRadius: isMobile ? 2 : 3 }}>
          <CardContent sx={{ p: isMobile ? 3 : 6, textAlign: 'center' }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: isMobile ? 1.5 : 2,
                opacity: 0.6,
              }}
            >
              <TrendingUp sx={{ fontSize: isMobile ? 48 : 64, color: 'text.secondary' }} />
              <Typography variant={isMobile ? 'body1' : 'h6'} color='text.secondary'>
                📊 포스팅된 콘텐츠가 없습니다
              </Typography>
              <Typography variant={isMobile ? 'caption' : 'body2'} color='text.secondary'>
                소셜 포스팅을 작성하면 여기에서 관리할 수 있습니다.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={isMobile ? 2 : 3}>
          {postedContents.map((post: any, index: number) => {
            const platformInfo = getPlatformInfo(post.platform);
            const status = getPostStatus(post);
            const PlatformIcon = platformInfo.icon;

            return (
              <Grid item xs={12} md={6} lg={4} key={post.id || index}>
                <Card
                  sx={{
                    borderRadius: isMobile ? 2 : 3,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: isMobile ? 'none' : 'translateY(-4px)',
                      boxShadow: isMobile ? 'none' : '0 8px 25px rgba(0,0,0,0.15)',
                    },
                  }}
                >
                  <CardContent sx={{ p: isMobile ? 2 : 3, flexGrow: 1 }}>
                    {/* 헤더 */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: isMobile ? 1.5 : 2,
                      }}
                    >
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 0.5 : 1 }}
                      >
                        <PlatformIcon
                          sx={{ color: platformInfo.color, fontSize: isMobile ? 18 : 20 }}
                        />
                        <Typography variant={isMobile ? 'body2' : 'subtitle2'} fontWeight='600'>
                          {platformInfo.name}
                        </Typography>
                      </Box>
                      <Typography
                        variant={isMobile ? 'caption' : 'body2'}
                        color={status.color as any}
                        sx={{ fontWeight: 600 }}
                      >
                        {status.label}
                      </Typography>
                    </Box>

                    {/* 내용 미리보기 */}
                    <Typography
                      variant={isMobile ? 'caption' : 'body2'}
                      sx={{
                        mb: isMobile ? 1.5 : 2,
                        display: '-webkit-box',
                        WebkitLineClamp: isMobile ? 2 : 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: 1.5,
                      }}
                    >
                      {post.content || post.text || '내용 없음'}
                    </Typography>

                    {/* 이미지 미리보기 */}
                    {post.images &&
                      Array.isArray(post.images) &&
                      post.images.length > 0 && (
                        <Box sx={{ mt: isMobile ? 1.5 : 2 }}>
                          <Typography
                            variant='caption'
                            color='text.secondary'
                            sx={{ mb: isMobile ? 0.5 : 1, display: 'block', fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                          >
                            첨부 이미지 ({post.images.length}개)
                          </Typography>
                          <Box
                            sx={{ display: 'flex', gap: isMobile ? 0.5 : 1, flexWrap: 'wrap' }}
                          >
                            {(post.images as string[])
                              .slice(0, isMobile ? 2 : 3)
                              .map((image: string, imgIndex: number) => (
                                <Box
                                  key={imgIndex}
                                  component='img'
                                  src={image}
                                  alt={`포스트 이미지 ${imgIndex + 1}`}
                                  sx={{
                                    width: isMobile ? 50 : 60,
                                    height: isMobile ? 50 : 60,
                                    objectFit: 'cover',
                                    borderRadius: 1,
                                    border: '1px solid #e0e0e0',
                                  }}
                                />
                              ))}
                            {post.images.length > (isMobile ? 2 : 3) && (
                              <Box
                                sx={{
                                  width: isMobile ? 50 : 60,
                                  height: isMobile ? 50 : 60,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  bgcolor: 'grey.100',
                                  borderRadius: 1,
                                  border: '1px solid #e0e0e0',
                                }}
                              >
                                <Typography
                                  variant='caption'
                                  color='text.secondary'
                                  sx={{ fontSize: isMobile ? '0.6rem' : '0.75rem' }}
                                >
                                  +{post.images.length - (isMobile ? 2 : 3)}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      )}

                    {/* 메타 정보 */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: isMobile ? 1 : 2,
                        mb: isMobile ? 1.5 : 2,
                        flexWrap: isMobile ? 'wrap' : 'nowrap',
                      }}
                    >
                      <Typography 
                        variant='caption' 
                        color='text.secondary'
                        sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                      >
                        📅 {formatDate(post.createdAt || post.postedAt || '')}
                      </Typography>
                      {post.engagement && (
                        <Typography 
                          variant='caption' 
                          color='text.secondary'
                          sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                        >
                          👍 {post.engagement.likes || 0} • 💬{' '}
                          {post.engagement.comments || 0}
                        </Typography>
                      )}
                    </Box>

                    {/* 오류 메시지 */}
                    {post.error && (
                      <Box
                        sx={{
                          p: isMobile ? 1 : 1.5,
                          bgcolor: 'error.50',
                          borderRadius: isMobile ? 1 : 2,
                          border: '1px solid',
                          borderColor: 'error.200',
                          mb: isMobile ? 1.5 : 2,
                        }}
                      >
                        <Typography 
                          variant='caption' 
                          color='error.main'
                          sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                        >
                          ⚠️ {post.error}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>

                  {/* 액션 버튼 */}
                  <Box
                    sx={{
                      p: isMobile ? 1.5 : 2,
                      pt: 0,
                      display: 'flex',
                      justifyContent: 'space-between',
                      borderTop: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Button
                      size='small'
                      startIcon={<Visibility sx={{ fontSize: isMobile ? 16 : 18 }} />}
                      onClick={() => {
                        setSelectedPost(post);
                        setPreviewOpen(true);
                      }}
                      sx={{ 
                        borderRadius: isMobile ? 1 : 2,
                        fontSize: isMobile ? '0.75rem' : '0.875rem',
                        px: isMobile ? 1 : 2
                      }}
                    >
                      상세보기
                    </Button>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* 상세보기 다이얼로그 */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth='md'
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ p: isMobile ? 2 : 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 1 : 2 }}>
            {selectedPost && (
              <>
                {React.createElement(
                  getPlatformInfo(selectedPost.platform).icon,
                  {
                    sx: { 
                      color: getPlatformInfo(selectedPost.platform).color,
                      fontSize: isMobile ? 20 : 24
                    },
                  },
                )}
                <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight='700'>
                  {getPlatformInfo(selectedPost.platform).name} 포스팅 상세
                </Typography>
              </>
            )}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: isMobile ? 2 : 3 }}>
          {selectedPost && (
            <Box>
              <Typography
                variant={isMobile ? 'body2' : 'body1'}
                sx={{ mb: isMobile ? 1.5 : 2, whiteSpace: 'pre-wrap' }}
              >
                {selectedPost.content || selectedPost.text}
              </Typography>

              {selectedPost.images &&
                Array.isArray(selectedPost.images) &&
                selectedPost.images.length > 0 && (
                  <Box sx={{ mt: isMobile ? 1.5 : 2 }}>
                    <Typography variant={isMobile ? 'body2' : 'subtitle2'} sx={{ mb: isMobile ? 0.5 : 1 }}>
                      첨부 이미지 ({selectedPost.images.length}개)
                    </Typography>
                    <Box sx={{ display: 'flex', gap: isMobile ? 0.5 : 1, flexWrap: 'wrap' }}>
                      {(selectedPost.images as string[]).map(
                        (image: string, index: number) => (
                          <Box
                            key={index}
                            component='img'
                            src={image}
                            alt={`이미지 ${index + 1}`}
                            sx={{
                              width: isMobile ? 60 : 80,
                              height: isMobile ? 60 : 80,
                              objectFit: 'cover',
                              borderRadius: 1,
                              border: '1px solid #e0e0e0',
                            }}
                          />
                        ),
                      )}
                    </Box>
                  </Box>
                )}

              <Divider sx={{ my: isMobile ? 1.5 : 2 }} />

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: isMobile ? 'flex-start' : 'center',
                  flexWrap: 'wrap',
                  gap: isMobile ? 1 : 2,
                  flexDirection: isMobile ? 'column' : 'row',
                }}
              >
                <Typography variant={isMobile ? 'caption' : 'body2'} color='text.secondary'>
                  게시일:{' '}
                  {formatDate(
                    selectedPost.createdAt || selectedPost.postedAt || '',
                  )}
                </Typography>
                
                {selectedPost.engagement && (
                  <Box sx={{ display: 'flex', gap: isMobile ? 1 : 2 }}>
                    <Typography variant={isMobile ? 'caption' : 'body2'} color='text.secondary'>
                      👍 {selectedPost.engagement.likes || 0}
                    </Typography>
                    <Typography variant={isMobile ? 'caption' : 'body2'} color='text.secondary'>
                      💬 {selectedPost.engagement.comments || 0}
                    </Typography>
                    <Typography variant={isMobile ? 'caption' : 'body2'} color='text.secondary'>
                      📤 {selectedPost.engagement.shares || 0}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: isMobile ? 2 : 3 }}>
          {/* 포스팅 보기 버튼 - postUrl이 있을 때만 표시 */}
            <Button 
              onClick={() => window.open(selectedPost.postUrl, '_blank')}
              variant="outlined"
              size={isMobile ? 'small' : 'medium'}
              sx={{ 
                fontSize: isMobile ? '0.875rem' : '1rem',
              }}
              style={{
                backgroundColor: 'white',
                color: 'black',
              }}
            >
              포스팅 보기
            </Button>
          <Button 
            onClick={() => setPreviewOpen(false)}
            size={isMobile ? 'small' : 'medium'}
            sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}
          >
            닫기
          </Button>
        </DialogActions>
      </Dialog>

      {/* 삭제 확인 다이얼로그 */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ p: isMobile ? 2 : 3 }}>
          <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight='700'>
            ⚠️ 포스팅 삭제 확인
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: isMobile ? 2 : 3 }}>
          <Typography variant={isMobile ? 'body2' : 'body1'}>
            이 포스팅을 정말 삭제하시겠습니까?
          </Typography>
          <Typography variant={isMobile ? 'caption' : 'body2'} color='text.secondary' sx={{ mt: 1 }}>
            삭제된 포스팅은 복구할 수 없습니다.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: isMobile ? 2 : 3, gap: isMobile ? 1 : 0 }}>
          <Button 
            onClick={() => setDeleteConfirmOpen(false)}
            size={isMobile ? 'small' : 'medium'}
            sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}
          >
            취소
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color='error'
            variant='contained'
            disabled={loading}
            size={isMobile ? 'small' : 'medium'}
            startIcon={loading ? <CircularProgress size={isMobile ? 14 : 16} /> : <Delete />}
            sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}
          >
            {loading ? '삭제 중...' : '삭제'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PostingManager;
